import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../api/generated';
import * as components from '../../../components';
import { KendoLoading } from '../../../components';
import PageHeader from '../../../components/layout/PageHeader';
import * as models from '../../../models';
import { IOrganization } from '../../../models';
import { setOrganizationName } from '../../../utils';

const validationSchema = Yup.object<models.IOrganization>().shape({
    name: Yup.string().required('Laukas yra privalomas'),
    address: Yup.string().required('Laukas yra privalomas')
});

const styles = fabric.mergeStyleSets({
    item: {
        padding: 5,
        margin: 10
    }
});

const OrganizationInformationPage: FC = () => {
    const [organizationForm, setOrganizationForm] = useState<IOrganization | null>(null);
    const [logoFilePreview, setLogoFilePreview] = useState<string | null>(null);
    const { organizationInformationId } = useParams<'organizationInformationId'>();

    const fetchData = async () => {
        try {
            const resp = await api.administration.getOrganization(parseInt(organizationInformationId.toString()));
            if (resp) {
                if (resp.logoPreview === null) {
                    setLogoFilePreview(null);
                } else {
                    setLogoFilePreview(resp.logoPreview);
                }
            }

            setOrganizationForm(resp);
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleImageSubmit = async (logoFile: File | null) => {
        try {
            await api.administration.saveOrganizationLogo({ id: parseInt(organizationInformationId.toString()), file: logoFile ?? new File([], '') });
            toast.success('Paveikslėlis sėkmingai išsaugotas');
        } catch (ex) {
            toast.error('Paveikslėlio išsaugoti nepavyko');
        }
    };

    const handleSubmit = async (values: IOrganization, formikActions: formik.FormikHelpers<IOrganization>) => {
        formikActions.setStatus();
        try {
            await api.administration.updateOrganization(values);

            setOrganizationName(values.name ? values.name : '');
            toast.success('Organizacijos pakeitimai išsaugoti');
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            await fetchData();
            formikActions.setSubmitting(false);
            formikActions.resetForm();
        }
    };
    return (
        <>
            <PageHeader text="Organizacijos informacija" />
            {!organizationForm && <KendoLoading />}
            {organizationForm && (
                <formik.Formik
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    initialValues={{ ...organizationForm }}
                    onSubmit={handleSubmit}
                    render={(props) => (
                        <formik.Form>
                            <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                                <formik.Field
                                    name="name"
                                    render={({ field, form }: formik.FieldProps<string>) => (
                                        <fabric.TextField
                                            {...field}
                                            className={styles.item}
                                            label="Organizacijos pavadinimas:"
                                            errorMessage={(props.status && props.status.name) || (form.errors.name && form.errors.name)}
                                            underlined
                                        />
                                    )}
                                />
                                <div className="ms-Grid-row" style={{ margin: 'auto' }}>
                                    <components.ImageInput
                                        inputKey={0}
                                        label={`Organizacijos logotipas:`}
                                        imagePreview={logoFilePreview}
                                        setImagePreview={(result: any) => setLogoFilePreview(result)}
                                        onChange={async (response: File | null) => await handleImageSubmit(response)}
                                    />
                                </div>
                                <div className="ms-Grid-row" style={{ textAlign: 'center', margin: '30px' }}>
                                    <fabric.PrimaryButton
                                        className="ms-slideRightIn40 ms-textAlignRight"
                                        text="Išsaugoti"
                                        disabled={props.isSubmitting || !props.isValid || !props.dirty}
                                        type="submit"
                                    />
                                </div>
                            </div>
                        </formik.Form>
                    )}
                />
            )}
        </>
    );
};

export default OrganizationInformationPage;
